import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import authService from '../services/authService';
import { toast } from 'react-toastify';
import AdminLayout from '../components/AdminLayout';

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  is_superuser: boolean;
  last_login: string | null;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    is_active: true,
    is_superuser: false,
    phone_number: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = authService.getToken();
      const response = await axios.get<User[]>(
        `${API_BASE_URL}/admin/users`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUsers(response.data);
    } catch (err) {
      toast.error('Failed to load users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUserSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = authService.getToken();
      await axios.post(
        `${API_BASE_URL}/users/`,
        {
          name: newUserData.name,
          email: newUserData.email,
          password: newUserData.password,
          role: newUserData.role,
          is_active: newUserData.is_active,
          is_superuser: newUserData.is_superuser,
          phone_number: newUserData.phone_number || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      toast.success('User created successfully');
      setShowAddModal(false);
      // Reset form
      setNewUserData({
        name: '',
        email: '',
        password: '',
        role: 'user',
        is_active: true,
        is_superuser: false,
        phone_number: '',
      });
      fetchUsers();
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to create user';
      toast.error(errorMsg);
      console.error('Error creating user:', err);
    }
  };

  const toggleUserStatus = async (userId: number, activate: boolean) => {
    try {
      const token = authService.getToken();
      const endpoint = activate ? 'activate' : 'deactivate';
      
      await axios.post(
        `${API_BASE_URL}/admin/users/${userId}/${endpoint}`,
        {
          is_active: activate,
          reason: 'Admin action from dashboard',
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      toast.success(`User ${activate ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user status');
      console.error('Error updating user:', err);
    }
  };

  const resetPassword = async (userId: number, userEmail: string) => {
    if (!window.confirm(`Reset password for ${userEmail}?`)) {
      return;
    }

    try {
      const token = authService.getToken();
      const response = await axios.post(
        `${API_BASE_URL}/admin/users/${userId}/reset-password`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Create password reset link
      const resetToken = response.data.token;
      const resetLink = `${window.location.origin}/reset-password?token=${resetToken}`;
      
      // Show modal with copy functionality
      showResetLinkModal(userEmail, resetLink);
      toast.success('Password reset link generated');
    } catch (err) {
      toast.error('Failed to reset password');
      console.error('Error resetting password:', err);
    }
  };

  const showResetLinkModal = (userEmail: string, resetLink: string) => {
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-semibold text-gray-900">Password Reset Link Generated</h3>
          <button onclick="this.closest('.fixed').remove()" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div class="mb-4">
          <p class="text-gray-600 mb-2">Password reset link for <strong>${userEmail}</strong>:</p>
          <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 break-all text-sm font-mono">
            ${resetLink}
          </div>
        </div>
        
        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div class="flex items-start">
            <svg class="w-5 h-5 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div class="text-sm text-yellow-800">
              <p class="font-medium">Important:</p>
              <ul class="list-disc list-inside mt-1 space-y-1">
                <li>This link expires in 24 hours</li>
                <li>It can only be used once</li>
                <li>Share this link securely with the user</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div class="flex gap-3">
          <button 
            onclick="navigator.clipboard.writeText('${resetLink}').then(() => { 
              const btn = this;
              const originalText = btn.innerHTML;
              btn.innerHTML = '<svg class=\\'w-5 h-5 inline mr-2\\' fill=\\'none\\' stroke=\\'currentColor\\' viewBox=\\'0 0 24 24\\'><path stroke-linecap=\\'round\\' stroke-linejoin=\\'round\\' stroke-width=\\'2\\' d=\\'M5 13l4 4L19 7\\' /></svg>Copied!';
              btn.classList.remove('bg-whatsapp-green', 'hover:bg-green-600');
              btn.classList.add('bg-green-600');
              setTimeout(() => {
                btn.innerHTML = originalText;
                btn.classList.add('bg-whatsapp-green', 'hover:bg-green-600');
                btn.classList.remove('bg-green-600');
              }, 2000);
            })"
            class="flex-1 bg-whatsapp-green text-white px-6 py-3 rounded-lg hover:bg-green-600 transition font-medium"
          >
            <svg class="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy Link
          </button>
          <button 
            onclick="this.closest('.fixed').remove()"
            class="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium text-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  };

  const filteredUsers = users.filter(user => {
    if (filter === 'active') return user.is_active;
    if (filter === 'inactive') return !user.is_active;
    return true;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="animate-pulse">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">

        {/* Filters & Add User Button */}
        <div className="mb-6 flex justify-between items-center flex-wrap gap-4">
          <div className="flex gap-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === 'all'
                  ? 'bg-whatsapp-green text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              All Users ({users.length})
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === 'active'
                  ? 'bg-whatsapp-green text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              Active ({users.filter(u => u.is_active).length})
            </button>
            <button
              onClick={() => setFilter('inactive')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filter === 'inactive'
                  ? 'bg-whatsapp-green text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              Inactive ({users.filter(u => !u.is_active).length})
            </button>
          </div>

          <button
            onClick={() => setShowAddModal(true)}
            className="bg-whatsapp-green hover:bg-green-600 text-white font-semibold px-4 py-2 rounded-lg transition duration-150 flex items-center gap-2 shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add User
          </button>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.is_superuser
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.is_superuser ? 'Admin' : user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.last_login
                      ? new Date(user.last_login).toLocaleString()
                      : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => toggleUserStatus(user.id, !user.is_active)}
                        className={`px-3 py-1 rounded ${
                          user.is_active
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => resetPassword(user.id, user.email)}
                        className="px-3 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                      >
                        Reset Password
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No users found
            </div>
          )}
        </div>

        {/* Add User Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fade-in">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 overflow-y-auto max-h-[90vh]">
              <div className="flex items-center justify-between mb-4 border-b pb-3">
                <h3 className="text-xl font-semibold text-gray-900">Add New User</h3>
                <button 
                  onClick={() => setShowAddModal(false)} 
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleAddUserSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={newUserData.name}
                    onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-whatsapp-green focus:border-whatsapp-green text-sm"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-whatsapp-green focus:border-whatsapp-green text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    value={newUserData.password}
                    onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-whatsapp-green focus:border-whatsapp-green text-sm"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number (Optional)</label>
                  <input
                    type="text"
                    value={newUserData.phone_number}
                    onChange={(e) => setNewUserData({ ...newUserData, phone_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-whatsapp-green focus:border-whatsapp-green text-sm"
                    placeholder="+254700000000"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <select
                      value={newUserData.role}
                      onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-whatsapp-green focus:border-whatsapp-green text-sm bg-white"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div className="flex items-center mt-6">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={newUserData.is_active}
                      onChange={(e) => setNewUserData({ ...newUserData, is_active: e.target.checked })}
                      className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900 font-medium">
                      Active Account
                    </label>
                  </div>
                </div>

                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    id="is_superuser"
                    checked={newUserData.is_superuser}
                    onChange={(e) => setNewUserData({ ...newUserData, is_superuser: e.target.checked })}
                    className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                  />
                  <label htmlFor="is_superuser" className="ml-2 block text-sm text-gray-900 font-medium">
                    Grant Superuser / Admin Panel Privileges
                  </label>
                </div>

                <div className="flex gap-3 justify-end pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-whatsapp-green hover:bg-green-600 text-white rounded-md transition font-medium text-sm shadow-sm"
                  >
                    Create User
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
